import { Check, Dot, FolderCheck, SquareCheckBig, X } from "lucide-react";
import React from "react";
import { createPortal } from "react-dom";
import { Tree } from "src/utils/Tree";
import styled from "styled-components";
import { Task } from "src/components/Task";
import { TaskField } from "src/components/TaskField";
import { TaskBody } from "./TaskBody";
import { withBlocks } from "src/utils/withBlock";
import { AnyActionableItem, TreeOf } from "src/types";
import { updateItem } from "src/database/queries/items";
import { produce } from "immer";
import { useGoalSpotlight } from "../GoalSpotlightContext";

interface Props {
  items: TreeOf<AnyActionableItem>[];
  date: string;
  onEnterTask(taskId: string): void;
}

export function TaskFocus({ items, date, onEnterTask }: Props) {
  const { recordGoalTaskCompletion } = useGoalSpotlight();
  const blurCaught = React.useRef(false);
  const [drawer, setDrawer] = React.useState<string | null>(null);
  const drawerTask = drawer && Tree.find(items, (n) => n.id === drawer);

  const catchBlur = () => {
    blurCaught.current = true;
    setTimeout(() => {
      blurCaught.current = false;
    }, 100);
  };

  const closeDrawer = () => {
    setDrawer(null);
  };

  const shadowClose = () => {
    // prevent race condition with input blurring
    // causing mis-click
    if (!blurCaught.current) {
      closeDrawer();
    }
  };

  const completeOpenTask = () => {
    if (drawerTask) {
      toggleCompleted(drawerTask);
      closeDrawer();
    }
  };

  const toggleCompleted = (item: AnyActionableItem) => {
    const completing = !item.completed;
    if (completing && item.type === "instance" && item.goalId) {
      recordGoalTaskCompletion(item.goalId);
    }
    updateItem({ ...item, completed: !item.completed });
  };

  const openTask = (taskId: string) => {
    const task = Tree.find(items, (t) => t.id === taskId);
    if (!task) return;
    if (task.children.length > 0) {
      onEnterTask(task.id);
    } else {
      setDrawer(task.id);
    }
  };

  const handleUpdateDrawerTask = (fn: (task: AnyActionableItem) => void) => {
    if (!drawerTask) return;
    const updatedTask = produce(drawerTask, fn);
    updateItem(updatedTask);
  };

  return (
    <>
      <Container>
        <Tasks>
          {items.map((task) => {
            return (
              <TaskWrapper key={task.id}>
                <Task item={task} onClick={() => openTask(task.id)}>
                  {withBlocks(task.content, "checkListItem", (items) => (
                    <TaskField icon={SquareCheckBig} data-testid="task-checklist-count">
                      {items.filter((t) => t.props.checked).length}/{items.length}
                    </TaskField>
                  ))}
                  {task.children.length > 0 && (
                    <TaskField icon={FolderCheck}>
                      {task.children.filter((t) => t.completed).length}/{task.children.length}
                    </TaskField>
                  )}
                  {task.isFolder ? (
                    <TaskField style={{ width: "34px" }} />
                  ) : (
                    <TaskField
                      title={task.completed ? "Mark as incomplete" : "Mark as complete"}
                      data-testid="task-complete-toggle"
                      highlight={task.completed}
                      icon={task.completed ? Check : Dot}
                      onClick={() => toggleCompleted(task)}
                      style={{ padding: "10px", marginRight: "-8px" }}
                    />
                  )}
                </Task>
              </TaskWrapper>
            );
          })}
        </Tasks>
      </Container>
      {drawerTask &&
        createPortal(
          <Shadow>
            <Backdrop onClick={shadowClose} />
            <Drawer
              data-testid="task-drawer"
              onClick={(e) => {
                e.stopPropagation();
              }}
              onBlur={catchBlur}
            >
              <DrawerActions>
                {!drawerTask.isFolder && (
                  <DrawerAction onClick={completeOpenTask}>
                    <Check color="white" size={20} />
                  </DrawerAction>
                )}
                <DrawerAction onClick={closeDrawer}>
                  <X color="white" size={20} />
                </DrawerAction>
              </DrawerActions>
              <TaskBody
                date={date}
                task={drawerTask}
                updateTask={(fn) => handleUpdateDrawerTask(fn)}
              />
            </Drawer>
          </Shadow>,
          document.body,
        )}
    </>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Tasks = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const TaskWrapper = styled.div`
  margin-bottom: -1px;
`;

const Shadow = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: rgba(0, 0, 0, 0.125);
  display: flex;
  flex-direction: column;
`;

const Backdrop = styled.div`
  flex-grow: 1;
`;

const Drawer = styled.div`
  max-height: 100%;
  position: relative;
  background: var(--box-color-hover);
  // min-height: 30%;
  padding-top: 10px;
  padding-bottom: 10px;
`;

const DrawerActions = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  margin-top: 20px;
  margin-right: 20px;
  display: flex;
  gap: 10px;
`;

const DrawerAction = styled.div`
  position: relative;
  width: 35px;
  height: 35px;
  background: var(--theme-color-primary);
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover:after,
  &:active:after {
    content: "";
    pointer-events: none;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background: rgba(0, 0, 0, 0.125);
  }
`;
